<template>
    <!-- Anchor stays in the form so we can locate the owning title input; the
         popup itself is teleported to .page (which carries the theme-* class,
         so theme CSS variables still apply) to escape any ancestor overflow /
         clip-path / transform, and positioned via inline fixed coordinates. -->
    <div ref="rootEl" class="lfmpub-form__similar-anchor">
        <Teleport to=".page">
            <div
                v-if="show && candidates.length > 0"
                ref="popupEl"
                class="lfmpub-form__similar"
                :style="popupStyle"
            >
                <div class="lfmpub-form__similar-header">{{ header }}</div>
                <div class="lfmpub-form__similar-items">
                    <div v-for="row in candidates" :key="row.uid" class="lfmpub-form__similar-item">
                        <a href="#" @click.prevent="onClickLink(row)">{{ row.title }}</a>
                        <div v-if="row.type_name" class="lfmpub-form__similar-info lfmpub-form__similar-info--type">
                        {{ row.type_name }}<template v-if="row.parent_title"> von {{ row.parent_title }}</template>
                    </div>
                        <div v-if="row.isbn" class="lfmpub-form__similar-info lfmpub-form__similar-info--isbn">{{ row.isbn }}</div>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import {ref, watch, onMounted, onBeforeUnmount} from 'vue';

const props = defineProps({
    value: { type: String, default: '' },
    checkUrl: { type: String, default: '' },
    confirmModal: { type: Object, default: null },
    header: { type: String, required: true },
});

const candidates = ref([]);
const show = ref(false);
const rootEl = ref(null);
const popupEl = ref(null);
const popupStyle = ref({});
let abortController = null;
let debounceTimer = null;

watch(() => props.value, (newVal) => {
    clearTimeout(debounceTimer);
    const trimmed = (newVal || '').trim();
    if (!trimmed || !props.checkUrl) {
        candidates.value = [];
        show.value = false;
        return;
    }
    debounceTimer = setTimeout(() => checkSimilar(trimmed), 400);
});

async function checkSimilar(text) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const formData = new FormData();
    formData.append('text', text);

    try {
        const resp = await fetch(props.checkUrl, {
            method: 'post',
            body: formData,
            signal: abortController.signal,
        });
        const result = await resp.json();
        candidates.value = result.candidates || [];
        show.value = candidates.value.length > 0;
        if (show.value) updatePopupPosition();
    } catch (e) {
        if (e.name !== 'AbortError') console.error(e);
    }
}

// --- Positioning (fixed, anchored to the input; mirrors FormSelect) ---

/** The title input this popup belongs to (sibling inside the FormField wrapper). */
function ownInput() {
    return rootEl.value?.parentElement?.querySelector('input.form-control') ?? null;
}

function updatePopupPosition() {
    const input = ownInput();
    if (!input) return;
    const rect = input.getBoundingClientRect();
    popupStyle.value = {
        position: 'fixed',
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 1050,
    };
}

function onScrollOrResize() {
    if (show.value) updatePopupPosition();
}

// --- Dismissal ---

/**
 * Dismiss the popup on any pointer/focus outside of it — except the title
 * input itself, where we re-open it if there are still candidates to show.
 */
function onDocumentPointer(e) {
    const target = e.target;
    if (popupEl.value && popupEl.value.contains(target)) {
        return;
    }
    if (target === ownInput()) {
        show.value = candidates.value.length > 0;
        if (show.value) updatePopupPosition();
        return;
    }
    show.value = false;
}

watch(show, (val) => {
    if (val) {
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize);
    } else {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
    }
});

onMounted(() => {
    document.addEventListener('mousedown', onDocumentPointer, true);
    document.addEventListener('focusin', onDocumentPointer, true);
});

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', onDocumentPointer, true);
    document.removeEventListener('focusin', onDocumentPointer, true);
    window.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
    clearTimeout(debounceTimer);
    if (abortController) abortController.abort();
});

async function onClickLink(row) {
    if (!props.confirmModal) {
        window.location.href = row.link;
        return;
    }
    const result = await props.confirmModal.open({
        title: 'Datensatz wechseln',
        message: 'Beim klick auf "Weiter" werden Sie zu diesem Datensatz weitergeleitet.<br><strong>Bisherige Änderungen gehen dabei verloren.</strong>',
        yesText: 'Weiter',
        noText: 'Abbrechen',
    });
    if (result === 'accepted') {
        window.location.href = row.link;
    }
}
</script>
